/**
 * StudioWizard — 첫 진입 위자드 (앨범 0개일 때)
 *
 * Step 1: 참조 사진 업로드 (신랑/신부 탭)
 * Step 2: 역할 + 컨셉 선택 → 사진 생성
 */

import { useState, useCallback } from 'react'
import { ImagePlus } from 'lucide-react'
import { Button } from '@/components/ui'
import { useReferencePhotos } from '../hooks/useReferencePhotos'
import { usePromptBlocksGrouped } from '@/hooks/usePromptBlocks'
import { useGenerationForm } from '../hooks/useGenerationForm'
import { ReferenceUpload } from './ReferenceUpload'
import { RoleSelector } from './RoleSelector'
import { ReferencePhotoSelector } from './ReferencePhotoSelector'
import { ConceptSelector } from './ConceptSelector'
import { GenerateModal } from './GenerateModal'
import styles from './StudioWizard.module.css'

export function StudioWizard() {
  const refPhotos = useReferencePhotos()
  const { grouped, isLoading: blocksLoading } = usePromptBlocksGrouped()
  const form = useGenerationForm('COUPLE')

  const [step, setStep] = useState<1 | 2>(1)
  const [showGenerate, setShowGenerate] = useState(false)

  const hasPhotos = refPhotos.groomPhotos.length > 0 || refPhotos.bridePhotos.length > 0

  const handleNext = useCallback(() => {
    if (hasPhotos) setStep(2)
  }, [hasPhotos])

  return (
    <div className={styles.root}>
      {/* 헤더 */}
      <div className={styles.header}>
        <h1 className={styles.title}>AI 스튜디오 시작하기</h1>
        <p className={styles.subtitle}>
          {step === 1
            ? '먼저 신랑, 신부의 참조 사진을 등록해주세요'
            : '컨셉을 선택하고 사진을 생성해보세요'}
        </p>
      </div>

      {step === 1 ? (
        <>
          {/* Step 1: 참조 사진 업로드 */}
          <div className={styles.uploadSection}>
            <ReferenceUpload
              photos={refPhotos.photos}
              uploading={refPhotos.uploading}
              onUpload={refPhotos.upload}
              onDelete={refPhotos.remove}
              getByRole={refPhotos.getByRole}
            />
            <p className={styles.uploadHint}>각 역할당 최대 5장까지 등록 가능합니다</p>
          </div>

          <div className={styles.nav}>
            <Button
              variant="primary"
              onClick={handleNext}
              disabled={!hasPhotos}
              style={{ flex: 1 }}
            >
              다음 단계
            </Button>
          </div>
        </>
      ) : (
        <>
          {/* Step 2: 역할 + 컨셉 선택 */}
          <div className={styles.modeSection}>
            <span className={styles.sectionLabel}>역할 선택</span>
            <RoleSelector
              selected={form.role}
              onChange={form.setRole}
              hasGroom={form.hasGroom}
              hasBride={form.hasBride}
            />

            <span className={styles.sectionLabel}>참조 사진 선택</span>
            <ReferencePhotoSelector
              role={form.role}
              groomPhotos={form.groomPhotos}
              bridePhotos={form.bridePhotos}
              selectedGroomId={form.selectedGroomId}
              selectedBrideId={form.selectedBrideId}
              onSelectGroom={form.setSelectedGroomId}
              onSelectBride={form.setSelectedBrideId}
            />

            <span className={styles.sectionLabel}>컨셉 선택</span>
            <ConceptSelector
              grouped={grouped}
              selection={form.blockSelection}
              onChange={form.setBlockSelection}
              isLoading={blocksLoading}
            />

            <div style={{ paddingTop: 'var(--space-4)' }}>
              <Button
                variant="primary"
                onClick={() => setShowGenerate(true)}
                style={{ width: '100%' }}
              >
                <ImagePlus size={18} /> 사진 생성하기
              </Button>
            </div>
          </div>

          <div className={styles.nav}>
            <Button variant="secondary" onClick={() => setStep(1)} style={{ flex: 1 }}>
              이전
            </Button>
          </div>
        </>
      )}

      <GenerateModal open={showGenerate} onClose={() => setShowGenerate(false)} />
    </div>
  )
}
